// useReduxPersistSessionData.js

import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {storeKeyValueInSessionStorage} from '../redux/actions';
import {printError, printLog} from "../utils/AppUtils";
import {checkNull, checkNullJson, checkNullStr, getDefStr} from "../../common/fusion-ui/utils/ReactUtils";

const TAG = 'useReduxPersistSessionData.js';

/**
 * A hook to update & get session-storage data (persisted: remains intact on url-change
 * (works well only with react-router-dom.Link & useNavigate)).
 *
 * @example
 *     //import hook
 *     import {useReduxPersistSessionData} from "../../../redux/reduxHooks";
 *
 *     //Get session-data and essential methods from useReduxPersistSessionData.
 *     const {
 *         sessionData,
 *         updateSessionStoreFieldValue,
 *         getSessionStoreFieldValue
 *     } = useReduxPersistSessionData({
 *         keys: [Fields.searchInputValue, Fields.searchInputValue,],
 *         // defValues: ['', DEF_OPTION,], //EXPERIMENTAL
 *     });
 *
 *     //print session-data on change
 *     useEffect(() => {
 *          console.log('useEffect.sessionData:', sessionData);
 *     }, [sessionData]);
 *
 *     //Declare necessary fields
 *      const Fields = {
 *         searchInputValue: 'inputValue',
 *      };
 *
 *      //Input to demonstrate the use of getSessionFieldValue && updateSessionFieldValue methods
 *       <input
 *          value={getSessionFieldValue(Fields.searchInputValue, DEF_OPTION)}
 *          onChange={(e) => {
 *              updateSessionFieldValue(Fields.searchInputValue, e.target.value);
 *          }}/>
 *
 *     //Function to update session-field value.
 *     function updateSessionFieldValue(field, value) {
 *         updateSessionStoreFieldValue(field, value);
 *     }
 *
 *     //Function to get session-field value.
 *     function getSessionFieldValue(field, defValue) {
 *         return getSessionStoreFieldValue(field, defValue);
 *     }
 *
 * @param {array} keys An array of keys to update their values in session-storage on start-up.
 */
const useReduxPersistSessionData = ({keys = [], defValues = []}) => {
    const fun = 'useReduxPersistSessionData:';
    const dispatch = useDispatch();

    // const [sessionData, setSessionData] = useState({});
    const sessionData = {};


    // Get values from session store using useSelector
    keys.forEach((key, index) => {
        try {
            sessionData[key] = useSelector((state) => state.sessionStore[key]);
            // setSessionData(prevState => ({
            //     ...prevState, [key]: useSelector((state) => state.sessionStore[key])
            // }));
        } catch (error) {
            logErr(fun, 'keys.forEach.error:', error);
        }
    });

    // Load persisted session data on component mount
    useEffect(() => {
        try {
            const persistedSessionData = sessionStorage.getItem('persist:session');
            // log(fun, 'useEffect.dispatch.keys.persistedSessionData.parsed:', JSON.parse(persistedSessionData).root);
            if (checkNullStr(persistedSessionData)) {
                const root = JSON.parse(persistedSessionData).root;
                if (checkNullJson(root)) {
                    const {sessionStore} = root;
                    // log(fun, 'useEffect.dispatch.keys.sessionStore:', sessionStore);
                    keys.forEach((key) => {
                        const persistedValue = sessionStore[key];
                        if (checkNull(persistedValue)) {
                            dispatch(storeKeyValueInSessionStorage(key, persistedValue)); // Dispatch the action method
                        }
                        // log(fun, 'useEffect.dispatch.keys.forEach.persistedValue:', persistedValue);
                    });
                }
            }
        } catch (error) {
            logErr(fun, 'useEffect.keys.error:', error);
        }
    }, [dispatch, keys]);

    // Function to update session data
    function updateSessionStoreFieldValue(key, value) {
        // log(fun, 'updateSessionStoreFieldValue.key:', key, 'value:', value);
        dispatch(storeKeyValueInSessionStorage(key, value)); // Dispatch the action method
    };

    function getSessionStoreFieldValue(key, defValue) {
        // const value = useSelector((state) => state.sessionStore[key]);
        // return getDefStr(value, defValue);
        return getDefStr(useSelector((state) => state.sessionStore[key]), defValue);
        // return getDefStr(sessionData, key, defValue);
        // log(fun, 'getSessionStoreFieldValue.key:', key, 'value:', value, 'defValue:', defValue);
        // return useSelector((state) => getJsonValue(state.sessionStore, key, defValue));
        // const index = checkNullArr(keys) ? keys.indexOf(key) : null;
        // return useSelector((state) => checkNullStr(index)
        //     ? getDefStr(getArrIndexValue(defValues, index), state.sessionStore[key])
        //     : state.sessionStore[key]);
    };
    return {sessionData, updateSessionStoreFieldValue, getSessionStoreFieldValue};
};


function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

export {useReduxPersistSessionData};
